import moment from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/pl';
import { Column } from "primereact/column";
import { Dropdown } from 'primereact/dropdown';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { ReactComponent as ArrowDownIcon } from '../../assets/images/arrow_down.svg';
import { ReactComponent as ArrowUpIcon } from '../../assets/images/arrow_up.svg';
import { ReactComponent as DeviceErrorsIcon } from '../../assets/images/device-errors-widget-icon.svg';
import DataTable, {
    FILTER_TYPES,
    DataTableContext,
    DataTableProvider,
} from "../../component/datatable2";
import { MessageContext } from '../../context/MessageContext';
import { useDashboardData } from '../../context/DashboardContext';
import { EventService } from '../../service/EventService';
import { Constants } from '../../utils/Constants';
import { RenderedHelper } from '../../utils/render/RenderedHelper';
import './DeviceErrorsWidget.scss';
import './Widget.scss';


const DeviceErrorsWidget = () => {
    return (
        <DataTableProvider>
            <DeviceErrorsWidgetImpl />
        </DataTableProvider>
    );
};

const DeviceErrorsToolbar = ({
    rowsPerPageOptions,
    defaultSearchCriteria,
    refreshTimer,
    isCollapsed,
    toggleCollapse,
}) => {
    const { searchCriteria, setSearchCriteria } = useContext(DataTableContext);
    const { t } = useTranslation();

    return (
        <div className="flex align-items-center gap-2">
            {!isCollapsed && (
                <>
                    <span className="auto-refresh-timer">
                        {t('deviceErrorsWidget.autoRefresh.updateIn')}: <span className="timer-number">{refreshTimer}</span> {t('deviceErrorsWidget.autoRefresh.seconds')}
                    </span>

                    <Dropdown
                        className="small"
                        id="rowsPerPage"
                        options={rowsPerPageOptions}
                        value={searchCriteria?.rows ?? defaultSearchCriteria.rows}
                        onChange={e =>
                            setSearchCriteria(prev => ({
                                ...prev,
                                first: 0,
                                rows: e.value,
                                timestamp: Date.now(),
                            }))
                        }
                    />
                </>
            )}

            {isCollapsed ? (
                <ArrowUpIcon className="collapse-button" onClick={toggleCollapse} />
            ) : (
                <ArrowDownIcon className="collapse-button" onClick={toggleCollapse} />
            )}
        </div>
    );
};

const DeviceErrorsWidgetImpl = () => {

    const { t, i18n } = useTranslation();
    const isMounted = useRef(false);
    const rowsPerPageOptions = [5, 10, 20, 50];
    const { showErrorMessage } = useContext(MessageContext);
    const { setSearchCriteria } = useContext(DataTableContext);
    const { locationOptions, cityOptions, deviceOptions } = useDashboardData();
    const [eventTypeOptions, setEventTypeOptions] = useState([]);
    const [eventPriorityOptions, setEventPriorityOptions] = useState([]);
    const [eventStatusOptions, setEventStatusOptions] = useState([]);
    const [refreshTimer, setRefreshTimer] = useState(60);
    const [isCollapsed, setIsCollapsed] = useState(false);

    const defaultSearchCriteria = useMemo(() => ({
        first: 0,
        rows: 5,
        sortField: 'eventDateTime',
        sortOrder: -1,
        baseQuery: '',
        filters: {}
    }), []);

    useEffect(() => {
        isMounted.current = true;
        setSearchCriteria(defaultSearchCriteria);
        fetchEventTypeOptions();
        fetchEventPriorityOptions();
        fetchEventStatusOptions();
        return () => {
            isMounted.current = false;
        }
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setRefreshTimer(prev => {
                if (prev <= 1) {
                    setSearchCriteria(prev => ({ ...prev, timestamp: Date.now() }));
                    return 60;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, []);


    const apiValueFromConverter = useMemo(() =>
        value => moment(value).startOf('day').utc().format(Constants.DATE_TIME_FORMAT_GET),
        []
    );
    const apiValueToConverter = useMemo(() =>
        value => moment(value).endOf('day').utc().format(Constants.DATE_TIME_FORMAT_GET),
        []
    );
    const apiValueConverter = value => value.map(v => v.id).join(',');

    const filterConfig = useMemo(() => [
        {
            fieldName: 'eventDateTime',
            label: t('deviceErrorsWidget.eventDateTime'),
            type: FILTER_TYPES.DATE_TIME_RANGE,
            apiParamName: 'event_date_time',
            apiValueFromConverter,
            apiValueToConverter
        },
        {
            fieldName: 'eventType',
            label: t('deviceErrorsWidget.eventType'),
            type: FILTER_TYPES.LIST,
            apiParamName: 'event_types',
            params: eventTypeOptions,
            apiValueConverter
        },
        {
            fieldName: 'location.name',
            label: t('deviceErrorsWidget.location'),
            type: FILTER_TYPES.LIST,
            params: locationOptions,
            apiParamName: 'locations',
            apiValueConverter
        },
        {
            fieldName: 'location.address.city',
            label: t('deviceErrorsWidget.locationCity'),
            type: FILTER_TYPES.LIST,
            params: cityOptions,
            apiParamName: 'cities',
            apiValueConverter
        },
        {
            fieldName: 'description',
            label: t('deviceErrorsWidget.description'),
            type: FILTER_TYPES.TEXT,
            apiParamName: 'description'
        },
        {
            fieldName: 'device.name',
            label: t('deviceErrorsWidget.device'),
            type: FILTER_TYPES.LIST,
            params: deviceOptions,
            apiParamName: 'devices',
            apiValueConverter
        },

        {
            fieldName: 'priority',
            label: t('deviceErrorsWidget.priority'),
            type: FILTER_TYPES.LIST,
            apiParamName: 'priorities',
            params: eventPriorityOptions,
            apiValueConverter
        },
        {
            fieldName: 'status',
            label: t('deviceErrorsWidget.status'),
            type: FILTER_TYPES.LIST,
            apiParamName: 'statuses',
            params: eventStatusOptions,
            apiValueConverter
        },
    ], [eventTypeOptions, eventPriorityOptions, eventStatusOptions, locationOptions, cityOptions, deviceOptions]);

    const normalizeCriteria = (criteria) => ({
        ...defaultSearchCriteria,
        ...criteria,
        first: criteria?.first ?? defaultSearchCriteria.first,
        rows: criteria?.rows ?? defaultSearchCriteria.rows,
        sortField:
            !criteria?.sortField || criteria.sortField === 'null'
                ? defaultSearchCriteria.sortField
                : criteria.sortField,
        sortOrder:
            criteria?.sortOrder === null || criteria?.sortOrder === undefined
                ? defaultSearchCriteria.sortOrder
                : criteria.sortOrder,
        baseQuery: criteria?.baseQuery ?? defaultSearchCriteria.baseQuery,
        filters: criteria?.filters ?? defaultSearchCriteria.filters,
    });

    const fetchData = async (criteria) => {
        try {
            return await EventService.getEvents(normalizeCriteria(criteria));
        } catch (err) {
            showErrorMessage(t('deviceErrorsWidget.error.fetch'));
            return { data: [], totalRecords: 0 };
        }
    };

    const fetchEventTypeOptions = () => {
        EventService.getEventTypeList()
            .then(options => {
                setEventTypeOptions(options.map(o => ({ id: o, label: t(`EventType.${o}`) })));
            })
            .catch(err => {
                showErrorMessage(t('deviceErrorsWidget.error.fetchEventTypeOptions'));
            });
    }

    const fetchEventPriorityOptions = () => {
        EventService.getEventPriorityList()
            .then(options => {
                setEventPriorityOptions(options.map(o => ({ id: o, label: t(`EventPriority.${o}`) })));
            })
            .catch(err => {
                showErrorMessage(t('deviceErrorsWidget.error.fetchEventPriorityOptions'));
            });
    }
    const fetchEventStatusOptions = () => {
        EventService.getEventStatusList()
            .then(options => {
                setEventStatusOptions(options.map(o => ({ id: o, label: t(`EventStatus.${o}`) })));
            })
            .catch(err => {
                showErrorMessage(t('deviceErrorsWidget.error.fetchEventStatusOptions'));
            });
    }


    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    const tableToolbarLeft = (
        <div className='flex align-items-center gap-2'>
            <DeviceErrorsIcon />
            <span className='widget-name'>{t('deviceErrorsWidget.widgetName')}</span>
        </div>
    );

    const tableToolbarRight = (
        <DeviceErrorsToolbar
            rowsPerPageOptions={rowsPerPageOptions}
            defaultSearchCriteria={defaultSearchCriteria}
            refreshTimer={refreshTimer}
            isCollapsed={isCollapsed}
            toggleCollapse={toggleCollapse}
        />
    );

    return (
        <div className={`device-errors-widget widget data-card ${isCollapsed ? 'collapsed' : ''}`}>
            <DataTable
                tableToolbarLeft={tableToolbarLeft}
                tableToolbarRight={tableToolbarRight}
                globalSearchPosition="none"
                fetchData={fetchData}
                filterConfig={filterConfig}
                defaultSearchCriteria={defaultSearchCriteria}
                showRowsPerPage={false}
                showPaginator={false}
                minWidth="1100px"
            >
                <Column field="eventDateTime" header={t('deviceErrorsWidget.eventDateTime')} sortable sortField="eventDateTime" id="eventDateTime"
                    headerClassName="dt2-header-wrap"
                    body={row => RenderedHelper.formatTimestamp(row.eventDateTime)}
                    style={{ minWidth: '140px', maxWidth: '160px' }}
                />
                <Column field="eventType" header={t('deviceErrorsWidget.eventType')} sortable sortField="eventType" id="eventType"
                    headerClassName="dt2-header-wrap"
                    body={row => RenderedHelper.formatEnum(row.eventType, 'EventType')}
                    style={{ minWidth: '130px', maxWidth: '160px' }}
                />
                <Column field="location.name" header={t('deviceErrorsWidget.location')} sortable sortField="location.name" id="location"
                    style={{ minWidth: '140px', maxWidth: '180px' }}
                />
                <Column field="location.city" header={t('deviceErrorsWidget.locationCity')} sortable sortField="location.address.city" id="locationCity"
                    headerClassName="dt2-header-wrap"
                    style={{ minWidth: '120px', maxWidth: '150px' }}
                />
                <Column field="device.name" header={t('deviceErrorsWidget.device')} sortable sortField="device.name" id="device"
                    style={{ minWidth: '130px', maxWidth: '170px' }}
                />
                <Column field="description" header={t('deviceErrorsWidget.description')} sortable id="description"
                    style={{ minWidth: '180px', maxWidth: '260px' }}
                />
                <Column field="priority" header={t('deviceErrorsWidget.priority')} sortable sortField="priority" id="priority"
                    body={row => RenderedHelper.formatEnum(row.priority, 'EventPriority')}
                    style={{ minWidth: '110px', maxWidth: '140px' }}
                />
                <Column field="status" header={t('deviceErrorsWidget.status')} sortable sortField="status" id="status"
                    body={row => RenderedHelper.formatEnum(row.status, 'EventStatus')}
                    style={{ minWidth: '110px', maxWidth: '140px' }}
                />
            </DataTable>
        </div>
    )

}

export default DeviceErrorsWidget;

